// Professional A4 invoice PDF generator for merchants on MaxelPay.
//
// The invoice is the MERCHANT's document: it carries the merchant's logo (or
// business name) and contact details — never MaxelPay branding. MaxelPay only
// appears in a small fixed liability disclaimer near the footer.
//
// Self-contained: builds the full invoice document (issuer identity, billed-to /
// invoice-details columns, line-items table, totals, payment details, notes,
// terms, disclaimer, footer) from an `invoice` object plus an optional
// `merchant` object and triggers a download.
//
// All monetary values render through the shared formatters so the PDF matches
// on-screen numbers exactly. Every field access is defensive — missing data
// falls back to '', '—', or 0 so the output never shows "undefined"/"NaN".

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatUSD, formatCrypto } from './formatAmount.js';
import {
  tokenLogoUrls,
  chainLogoUrl,
  chainDisplayLabel,
  chainNativeSymbol,
  tokenColor,
  tokenInitials,
} from './assetLogos.js';

// --- Neutral, de-branded palette -------------------------------------------
// No merchant brand color is known, so the accent is a professional slate.
const INK = [15, 23, 42];          // #0f172a - headings, wordmark, total
const SLATE = [51, 65, 85];        // #334155 - accent rule + key figures
const BODY = [55, 65, 81];         // #374151 - body text
const MUTED = [107, 114, 128];     // #6b7280 - labels / secondary
const FAINT = [148, 163, 184];     // #94a3b8 - footer / fine print
const RULE = [226, 232, 240];      // #e2e8f0 - hairlines
const LIGHT = [248, 250, 252];     // #f8fafc - table header / panel fill

// Fixed liability disclaimer — always printed. MaxelPay is named here (legal
// fine print), which is intentional and separate from merchant branding.
const LIABILITY_DISCLAIMER =
  'Invoice generated by MaxelPay. MaxelPay does not own, guarantee, or hold any '
  + 'liability for this payment.';

// --- Text safe for the PDF's standard fonts --------------------------------
// jsPDF's built-in Helvetica is WinAnsi (cp1252). Anything outside that range
// is not dropped by jsPDF — it is written to the stream as raw bytes and comes
// out as garbage glyphs, and it is measured as a different number of
// characters than it draws. That is how a line can be laid out as fitting its
// column and then rendered wider than it, over the figures beside it.
//
// So: transliterate the characters merchants actually type, and drop anything
// else that the font cannot represent.
const WINANSI_HIGH = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030,
  0x0160, 0x2039, 0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022,
  0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
]);

const TRANSLIT = {
  '\u2192': '->', '\u2190': '<-', '\u2194': '<->', '\u21d2': '=>', '\u21d0': '<=',
  '\u2191': '^', '\u2193': 'v',
  '\u2713': 'Y', '\u2714': 'Y', '\u2717': 'x', '\u2718': 'x', '\u2705': 'Y', '\u274c': 'x',
  '\u2260': '!=', '\u2264': '<=', '\u2265': '>=', '\u2248': '~',
  '\u00a0': ' ', '\u2009': ' ', '\u200a': ' ', '\u202f': ' ', '\u2007': ' ',
  '\u200b': '', '\u200c': '', '\u200d': '', '\ufeff': '',
  '\u2212': '-', '\u2010': '-', '\u2011': '-', '\u2012': '-', '\u2015': '-',
  '\u00ab': '<<', '\u00bb': '>>', '\u2032': "'", '\u2033': '"',
};

const canDraw = (cp) =>
  (cp >= 0x20 && cp <= 0x7e) || (cp >= 0xa0 && cp <= 0xff) || WINANSI_HIGH.has(cp);

/**
 * Make a string safe to measure and draw. Strips control characters, folds
 * whitespace runs, transliterates what it can and drops what it cannot.
 * Everything a merchant or customer can type goes through this before it
 * reaches the page.
 */
function pdfText(value) {
  const raw = String(value ?? '');
  let out = '';
  for (const ch of raw) {
    if (Object.prototype.hasOwnProperty.call(TRANSLIT, ch)) {
      out += TRANSLIT[ch];
      continue;
    }
    const cp = ch.codePointAt(0);
    // Control ranges, and anything the font has no glyph for.
    if (cp < 0x20 || (cp >= 0x7f && cp <= 0x9f)) { out += ' '; continue; }
    if (!canDraw(cp)) continue;
    out += ch;
  }
  return out.replace(/\s+/g, ' ').trim();
}

// Status pill colors: [fill, text]. Semantic, not brand. Default falls back to gray.
const STATUS_COLORS = {
  paid: { fill: [16, 185, 129], text: [255, 255, 255] },
  overpaid: { fill: [16, 185, 129], text: [255, 255, 255] },
  overdue: { fill: [220, 38, 38], text: [255, 255, 255] },
  open: { fill: [217, 119, 6], text: [255, 255, 255] },
  partially_paid: { fill: [217, 119, 6], text: [255, 255, 255] },
  draft: { fill: [148, 163, 184], text: [255, 255, 255] },
  cancelled: { fill: [107, 114, 128], text: [255, 255, 255] },
};

const num = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Load a logo URL into a data URL usable by jsPDF.addImage.
// Works in the browser and in Node 18+ (fetch + arrayBuffer). Returns null on
// any failure or unsupported type so the caller falls back to a text wordmark.
async function loadImageDataUrl(url) {
  if (!url) return null;
  if (typeof url === 'string' && url.startsWith('data:')) return url;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const type = (res.headers.get('content-type') || '').toLowerCase();
    // jsPDF.addImage cannot rasterize SVG — bail so we fall back to text.
    if (type.includes('svg')) return null;
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    const b64 = typeof btoa !== 'undefined'
      ? btoa(binary)
      : Buffer.from(bytes).toString('base64');
    const mime = type || 'image/png';
    return `data:${mime};base64,${b64}`;
  } catch {
    return null;
  }
}

// Draw any image the browser can decode (SVG, WebP, PNG) onto a canvas and read
// it back as PNG, which is what jsPDF.addImage accepts. The source must be a
// data: URL — a remote URL would taint the canvas and make toDataURL throw.
// Returns null outside a browser (no canvas), where the caller falls back to a
// drawn glyph.
async function rasterizeToPng(dataUrl, px = 96) {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, px, px);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// Fetch a logo and return something jsPDF can embed.
//
// Only the icon CDN that sends `Access-Control-Allow-Origin` can be read at all
// from the browser — the bitmap CDNs send none, so fetch fails there however the
// bytes are handled. That open source serves SVG, and jsPDF cannot rasterize
// SVG, so the bytes go through a canvas first. A source that already serves PNG
// or JPEG is embedded directly (the path Node takes, where there is no canvas).
async function fetchLogoForPdf(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const type = (res.headers.get('content-type') || '').toLowerCase();
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    const b64 = typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(bytes).toString('base64');
    const dataUrl = `data:${type || 'image/png'};base64,${b64}`;

    if (type.includes('png') || type.includes('jpeg') || type.includes('jpg')) return dataUrl;
    return rasterizeToPng(dataUrl);
  } catch {
    return null;
  }
}

// Walk a list of candidate logo urls and return the first that yields an
// embeddable bitmap.
async function loadFirstImage(urls) {
  for (const url of urls.filter(Boolean)) {
    // eslint-disable-next-line no-await-in-loop
    const data = await fetchLogoForPdf(url);
    if (data) return data;
  }
  return null;
}

// Preload one logo per distinct asset and chain across the payments, so a
// 20-payment invoice still fetches only a couple of images.
async function loadAssetLogos(payments) {
  const tokens = [...new Set(payments.map((p) => p.symbol).filter(Boolean))];
  const chains = [...new Set(payments.map((p) => p.chain).filter(Boolean))];

  const [tokenPairs, chainPairs] = await Promise.all([
    Promise.all(tokens.map(async (sym) => [sym, await loadFirstImage(tokenLogoUrls(sym))])),
    Promise.all(
      chains.map(async (ch) => {
        // The chain icon CDN sends no CORS header, so its bytes are unreadable
        // from the browser. The chain's native asset gives the same recognisable
        // mark from the icon set that is readable (BNB for BSC, ETH for Sepolia).
        const native = chainNativeSymbol(ch);
        const candidates = native ? tokenLogoUrls(native) : [];
        return [ch, await loadFirstImage([...candidates, chainLogoUrl(ch)])];
      }),
    ),
  ]);

  return {
    tokens: Object.fromEntries(tokenPairs),
    chains: Object.fromEntries(chainPairs),
  };
}

// Draw a logo inside a table cell, falling back to the same coloured initials
// glyph the app uses when no bitmap could be fetched.
function drawCellLogo(doc, dataUrl, label, x, y, size) {
  if (dataUrl) {
    try {
      const props = doc.getImageProperties(dataUrl);
      doc.addImage(dataUrl, props.fileType || 'PNG', x, y, size, size);
      return;
    } catch {
      // Unsupported bitmap — fall through to the glyph.
    }
  }
  const [r, g, b] = tokenColor(label) ? hexToRgb(tokenColor(label)) : [148, 163, 184];
  doc.setFillColor(r, g, b);
  doc.circle(x + size / 2, y + size / 2, size / 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(size * 1.6);
  doc.setTextColor(255, 255, 255);
  doc.text(tokenInitials(label) || '?', x + size / 2, y + size / 2 + size * 0.28, {
    align: 'center',
  });
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : [148, 163, 184];
}

// Build the jsPDF document (async because the logo is fetched). Exported so the
// output can be rendered/inspected outside the browser; the browser entry point
// below wraps this and triggers a download.
export async function buildInvoiceDoc(invoice, merchant = {}, transactions = []) {
  const inv = invoice || {};
  const mer = merchant || {};
  const currency = inv.currency || 'USD';
  // USD amounts use 2-decimal fiat formatting; non-USD currencies trim like tokens.
  const money = (v) => (currency.toUpperCase() === 'USD' ? formatUSD(v) : formatCrypto(v));
  const withCcy = (v) => `${money(v)} ${currency}`;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // Document properties. A PDF has no favicon — the tab icon comes from the
  // viewer, not the file — but the Title is what a browser tab, a window title
  // bar and Finder's Get Info actually show, and it was unset, so the file
  // presented as a bare "INV-xxxxxx.pdf" with no identity. Author is the
  // merchant, because the invoice is the merchant's document; MaxelPay is the
  // Creator, i.e. the tool that produced it.
  doc.setProperties({
    title: `Invoice ${inv.id || ''}`.trim() + (mer.businessName ? ` - ${mer.businessName}` : ''),
    subject: `Invoice ${inv.id || ''} for ${inv.customer_name || 'customer'}`.trim(),
    author: (mer.businessName || '').trim() || 'MaxelPay merchant',
    creator: 'MaxelPay',
    keywords: ['invoice', inv.id, currency].filter(Boolean).join(', '),
  });

  const pageW = doc.internal.pageSize.getWidth();   // 210mm
  const pageH = doc.internal.pageSize.getHeight();   // 297mm
  const margin = 8;
  const contentR = pageW - margin;

  // Footer geometry, defined once. FOOTER_TOP is the highest ink the footer
  // block draws (the liability disclaimer); BODY_BOTTOM is the lowest y any
  // body content may reach, with a little air between the two. Everything that
  // flows - the item table, the payments table, the notes - is bounded by
  // these rather than by its own magic number, so the footer cannot be
  // overdrawn by content again.
  const FOOTER_BASE = pageH - 20;        // 277mm: the footer rule's anchor
  const FOOTER_TOP = FOOTER_BASE - 4;    // 273mm: first line of the disclaimer
  const BODY_BOTTOM = FOOTER_TOP - 6;    // 267mm: hard floor for body content
  // What autoTable needs as its bottom margin to respect that floor. Passing
  // only left/right left the bottom effectively unguarded - rows were drawn to
  // 282mm, 9mm into the disclaimer and across the footer rule.
  const TABLE_BOTTOM_MARGIN = pageH - BODY_BOTTOM;

  // Thin slate accent stripe along the very top.
  doc.setFillColor(...SLATE);
  doc.rect(0, 0, pageW, 3, 'F');

  // === Header: merchant identity (left) + INVOICE label (right) =============
  const headTop = 22;
  const logoData = await loadImageDataUrl(mer.logoUrl);
  // Everything a merchant or customer can type goes through pdfText before
  // it is measured or drawn.
  const businessName = pdfText(mer.businessName);

  let identityBottom = headTop; // y after the logo/wordmark block
  if (logoData) {
    try {
      const props = doc.getImageProperties(logoData);
      const maxW = 46;
      const maxH = 18;
      const ratio = props.width / props.height || 1;
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      doc.addImage(logoData, props.fileType || 'PNG', margin, headTop - 4, w, h);
      identityBottom = headTop - 4 + h;
    } catch {
      // Unsupported image — fall through to the wordmark below.
    }
  }
  if (identityBottom === headTop) {
    // No usable logo: business name as the wordmark (fallback to a neutral label).
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...INK);
    doc.text(businessName || 'Invoice', margin, headTop);
    identityBottom = headTop + 2;
  }

  // Issuer contact lines (compact) under the identity mark.
  const contactBits = [];
  if (mer.website) contactBits.push(String(mer.website).replace(/^https?:\/\//, ''));
  if (mer.email) contactBits.push(pdfText(mer.email));
  const contactLines = [];
  if (contactBits.length) contactLines.push(contactBits.join('  ·  '));
  const addressBit = [mer.address, mer.phone].filter(Boolean).join('  ·  ');
  if (addressBit) contactLines.push(addressBit);

  let cy = identityBottom + 5;
  if (contactLines.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    contactLines.forEach((line) => {
      doc.text(doc.splitTextToSize(line, 90), margin, cy);
      cy += 4.2;
    });
  }

  // Right side: large quiet "INVOICE" label, id, status pill.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...RULE);
  doc.text('INVOICE', contentR, headTop, { align: 'right' });

  let metaY = headTop + 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(inv.id || '', contentR, metaY, { align: 'right' });

  const statusKey = (inv.status || 'open').toLowerCase();
  const statusLabel = (inv.status || 'open').replace(/_/g, ' ').toUpperCase();
  const pill = STATUS_COLORS[statusKey] || STATUS_COLORS.draft;
  metaY += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const pillTextW = doc.getTextWidth(statusLabel);
  const pillPadX = 4;
  const pillW = pillTextW + pillPadX * 2;
  const pillH = 6;
  const pillX = contentR - pillW;
  doc.setFillColor(...pill.fill);
  doc.roundedRect(pillX, metaY - pillH + 1.5, pillW, pillH, 1.5, 1.5, 'F');
  doc.setTextColor(...pill.text);
  doc.text(statusLabel, pillX + pillPadX, metaY, { baseline: 'alphabetic' });

  // === Billed To / Invoice Details columns =================================
  let y = Math.max(56, cy + 6, metaY + 8);
  const colGap = 8;
  const colW = (contentR - margin - colGap) / 2;
  const rightX = margin + colW + colGap;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('BILLED TO', margin, y, { charSpace: 0.3 });
  doc.text('INVOICE DETAILS', rightX, y, { charSpace: 0.3 });

  // Hairline under the section labels.
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 2.5, margin + colW, y + 2.5);
  doc.line(rightX, y + 2.5, contentR, y + 2.5);

  // Left column: customer. Both fields are wrapped to the column width. They
  // were drawn as single unbounded lines, so a 39-character name at 11pt bold
  // measured 95mm against an 83mm column and ran 4mm into the INVOICE DETAILS
  // labels beside it. 40 characters is what the invoice form allows, so this is
  // reachable with a perfectly valid name, not just a pathological one.
  // splitTextToSize hard-breaks unbroken tokens too, which covers long emails.
  const NAME_LINE_H = 5.5;
  const EMAIL_LINE_H = 5;

  let leftY = y + 9;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  const nameLines = doc.splitTextToSize(pdfText(inv.customer_name) || 'Customer', colW);
  nameLines.forEach((line, i) => doc.text(line, margin, leftY + i * NAME_LINE_H));
  leftY += (nameLines.length - 1) * NAME_LINE_H;

  if (inv.customer_email) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...MUTED);
    const emailLines = doc.splitTextToSize(pdfText(inv.customer_email), colW);
    emailLines.forEach((line, i) => doc.text(line, margin, leftY + NAME_LINE_H + i * EMAIL_LINE_H));
    // leftY ends on the last baseline drawn, which is what tableStartY below
    // measures the table's offset from.
    leftY += NAME_LINE_H + (emailLines.length - 1) * EMAIL_LINE_H;
  }

  // Right column: labelled detail rows.
  const detailRows = [
    ['Invoice Number', inv.id || '—'],
    ['Issue Date', formatDate(inv.created_at)],
    ['Due Date', formatDate(inv.due_date)],
  ];
  let detY = y + 9;
  detailRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(label, rightX, detY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(String(value), contentR, detY, { align: 'right' });
    detY += 6.5;
  });

  // === Line items table (light, ruled — no heavy dark block) ================
  const ITEM_QTY_W = 18;
  const ITEM_PRICE_W = 36;
  const ITEM_AMT_W = 38;

  const items = Array.isArray(inv.items) ? inv.items : [];
  const tableStartY = Math.max(leftY, detY) + 8;

  const body = items.map((item) => {
    const qty = num(item?.quantity);
    // Invoices are stored with `unitPrice` (that is what the create form
    // sends and what the API validates). Reading only `price` printed every
    // unit price as 0.00 and, on any item without a stored `amount`, made the
    // amount fall back to qty x 0. `price` is still accepted for anything
    // older that used it.
    const price = num(item?.unitPrice ?? item?.price);
    const amount = item?.amount != null ? num(item.amount) : qty * price;
    return [
      pdfText(item?.description) || 'Item',
      qty ? String(qty) : '0',
      withCcy(price),
      withCcy(amount),
    ];
  });

  autoTable(doc, {
    startY: tableStartY,
    theme: 'plain',
    head: [['Description', 'Qty', 'Unit Price', 'Amount']],
    body: body.length ? body : [['No items', '0', withCcy(0), withCcy(0)]],
    margin: { left: margin, right: margin, bottom: TABLE_BOTTOM_MARGIN },
    styles: {
      fontSize: 9.5,
      cellPadding: { top: 3.2, bottom: 3.2, left: 4, right: 4 },
      textColor: BODY,
      lineColor: RULE,
      lineWidth: { bottom: 0.2 },
      // Wrap, never spill. Stated rather than left to the default so a later
      // style change cannot quietly turn it off.
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: LIGHT,
      textColor: MUTED,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      lineColor: RULE,
      lineWidth: { bottom: 0.4 },
    },
    bodyStyles: { lineColor: RULE, lineWidth: { bottom: 0.2 } },
    // The description takes exactly what the three numeric columns leave, as a
    // number rather than 'auto'. An auto column is sized from its content, so
    // a long description could be allotted more than the table has and draw
    // across the figures beside it.
    columnStyles: {
      0: { cellWidth: contentR - margin - (ITEM_QTY_W + ITEM_PRICE_W + ITEM_AMT_W) },
      1: { cellWidth: ITEM_QTY_W, halign: 'right' },
      2: { cellWidth: ITEM_PRICE_W, halign: 'right' },
      3: { cellWidth: ITEM_AMT_W, halign: 'right' },
    },
    // Right-align the numeric column headers (Qty / Unit Price / Amount) so they
    // line up with their values — columnStyles.halign doesn't reach head cells.
    didParseCell: (data) => {
      if (data.section === 'head' && data.column.index > 0) {
        data.cell.styles.halign = 'right';
      }
    },
  });

  // === Totals block (right-aligned) ========================================
  let ty = (doc.lastAutoTable?.finalY || tableStartY) + 10;
  const totalsLabelX = contentR - 70;
  const totalsValX = contentR;

  // Start a fresh page and reset the cursor. Used whenever the next block
  // would cross BODY_BOTTOM.
  const newPage = () => {
    doc.addPage();
    ty = margin + 6;
  };

  // Keep a fixed-height block whole: if it would cross BODY_BOTTOM, move it to
  // a new page. The totals and the payment-details box used to be drawn
  // wherever the item table happened to end, so an item table finishing low on
  // the page put "Total / Amount Paid / Amount Remaining" straight through the
  // disclaimer and footer.
  const keepTogether = (height) => {
    if (ty + height > BODY_BOTTOM) newPage();
  };

  const subtotal = inv.metadata?.subtotal != null
    ? num(inv.metadata.subtotal)
    : items.reduce(
      (s, it) => s + (it?.amount != null
        ? num(it.amount)
        : num(it?.quantity) * num(it?.unitPrice ?? it?.price)),
      0,
    );

  const totalRow = (label, value, opts = {}) => {
    const { bold = false, labelColor = MUTED, valueColor = INK, size = 9.5 } = opts;
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...labelColor);
    doc.text(label, totalsLabelX, ty);
    doc.setTextColor(...valueColor);
    doc.text(value, totalsValX, ty, { align: 'right' });
  };

  const gstAmount = num(inv.metadata?.gstAmount);

  // Height of the whole block from the Subtotal baseline to below the Amount
  // Remaining text: the row advances used below (6.5, [6.5 GST], 5, 8, 6.5)
  // plus the last line's own height.
  keepTogether(6.5 + (gstAmount > 0 ? 6.5 : 0) + 5 + 8 + 6.5 + 4);

  totalRow('Subtotal', withCcy(subtotal));
  ty += 6.5;

  if (gstAmount > 0) {
    const gstLabel = inv.metadata?.gstType === 'percentage'
      ? `GST/Tax (${inv.metadata?.gstValue ?? 0}%)`
      : 'GST/Tax';
    totalRow(gstLabel, withCcy(gstAmount));
    ty += 6.5;
  }

  // Divider before the bold total.
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.3);
  doc.line(totalsLabelX, ty - 1, totalsValX, ty - 1);
  ty += 5;

  totalRow('Total', withCcy(num(inv.amount)), { bold: true, labelColor: INK, size: 12 });
  ty += 8;

  const amountPaid = num(inv.amount_paid);
  totalRow('Amount Paid', withCcy(amountPaid), {
    // Green only when something was actually paid; otherwise muted.
    valueColor: amountPaid > 0 ? [16, 185, 129] : MUTED,
  });
  ty += 6.5;

  const remaining = inv.amount_remaining != null ? num(inv.amount_remaining) : num(inv.amount);
  totalRow('Amount Remaining', withCcy(remaining), { bold: true, labelColor: INK, valueColor: SLATE, size: 10.5 });
  ty += 14;

  // === Payment details =====================================================
  if (inv.network || inv.token || inv.receiving_address) {
    const boxX = margin;
    const boxW = contentR - margin;
    const rows = [];
    if (inv.network) rows.push(['Network', inv.network]);
    if (inv.token) rows.push(['Token', inv.token]);
    if (inv.receiving_address) rows.push(['Receiving Address', inv.receiving_address]);

    const headerH = 9;
    const rowH = 6.5;
    const boxH = headerH + rows.length * rowH + 4;

    keepTogether(boxH);

    doc.setFillColor(...LIGHT);
    doc.roundedRect(boxX, ty, boxW, boxH, 2, 2, 'F');

    let py = ty + 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    doc.text('PAYMENT DETAILS', boxX + 6, py, { charSpace: 0.3 });
    py += 6;

    rows.forEach(([label, value]) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...MUTED);
      doc.text(label, boxX + 6, py);
      doc.setTextColor(...BODY);
      // Constrain long values (addresses) to the box width.
      const valStartX = boxX + 42;
      const maxValW = boxX + boxW - 6 - valStartX;
      const valLines = doc.splitTextToSize(String(value), maxValW);
      doc.text(valLines, valStartX, py);
      py += rowH + (valLines.length > 1 ? (valLines.length - 1) * 4 : 0);
    });

    ty += boxH + 10;
  }

  // === Payment breakdown ===================================================
  // One row per on-chain payment that settled this invoice. An invoice is often
  // paid in several transfers (a $10 invoice paid as 2 x $5), and the summary
  // above cannot show that, so the asset and network of each payment are listed
  // here with their own logos rather than as bare text.
  const payments = (Array.isArray(transactions) ? transactions : [])
    .filter((t) => t && (t.status === 'succeeded' || t.status === 'confirmed' || t.amount != null))
    // Oldest first: the breakdown reads as the order the customer paid in, while
    // the API returns newest first for the on-screen table.
    .slice()
    .sort((a, b) => new Date(a.createdAt || a.created_at || 0) - new Date(b.createdAt || b.created_at || 0))
    .map((t) => ({
      symbol: (t.currency || inv.token || '').toString().toUpperCase(),
      chain: t.network || inv.network || '',
      tokenAmount: num(t.amount),
      usd: t.amountUsd != null ? num(t.amountUsd) : null,
      txHash: t.txHash || t.tx_hash || '',
      date: t.createdAt || t.created_at || null,
    }));

  if (payments.length) {
    const logos = await loadAssetLogos(payments);
    const LOGO = 4.2; // mm

    if (ty > pageH - 80) {
      doc.addPage();
      ty = margin + 6;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('PAYMENT BREAKDOWN', margin, ty, { charSpace: 0.3 });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BODY);
    doc.text(
      `${payments.length} ${payments.length === 1 ? 'transaction' : 'transactions'}`,
      contentR,
      ty,
      { align: 'right' },
    );
    ty += 4;

    const body = payments.map((p, i) => [
      String(i + 1),
      formatDate(p.date),
      p.symbol || '—',
      chainDisplayLabel(p.chain) || '—',
      p.txHash ? `${p.txHash.slice(0, 8)}…${p.txHash.slice(-6)}` : '—',
      `${formatCrypto(p.tokenAmount)} ${p.symbol}`.trim(),
      p.usd != null ? `$${formatUSD(p.usd)}` : '—',
    ]);

    const totalToken = payments.reduce((sum, p) => sum + p.tokenAmount, 0);
    const totalUsd = payments.reduce((sum, p) => sum + (p.usd ?? 0), 0);
    const sameSymbol = new Set(payments.map((p) => p.symbol)).size === 1;

    autoTable(doc, {
      startY: ty,
      theme: 'plain',
      head: [['#', 'DATE', 'ASSET', 'NETWORK', 'TX HASH', 'AMOUNT', 'VALUE (USD)']],
      body,
      foot: [[
        '',
        '',
        '',
        '',
        'Total received',
        sameSymbol ? `${formatCrypto(totalToken)} ${payments[0].symbol}`.trim() : '—',
        `$${formatUSD(totalUsd)}`,
      ]],
      margin: { left: margin, right: margin, bottom: TABLE_BOTTOM_MARGIN },
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 },
        textColor: BODY,
        lineColor: RULE,
        lineWidth: { bottom: 0.2 },
        overflow: 'hidden',
      },
      headStyles: {
        fillColor: LIGHT,
        textColor: MUTED,
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 },
        lineColor: RULE,
        lineWidth: { bottom: 0.4 },
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: INK,
        fontStyle: 'bold',
        fontSize: 8.5,
        lineColor: RULE,
        lineWidth: { top: 0.4 },
      },
      columnStyles: {
        0: { cellWidth: 8, textColor: MUTED },
        1: { cellWidth: 24 },
        // Room for the logo drawn in didDrawCell.
        2: { cellWidth: 26, cellPadding: { top: 2.6, bottom: 2.6, left: 3 + LOGO + 1.8, right: 3 } },
        3: { cellWidth: 30, cellPadding: { top: 2.6, bottom: 2.6, left: 3 + LOGO + 1.8, right: 3 } },
        4: { cellWidth: 'auto', textColor: MUTED },
        5: { cellWidth: 30, halign: 'right' },
        6: { cellWidth: 24, halign: 'right' },
      },
      didParseCell: (data) => {
        // Every section, not just the head. columnStyles' halign reaches the
        // body but NOT the foot in this version of jspdf-autotable, so the
        // totals rendered left-aligned inside right-aligned columns and sat
        // short of the AMOUNT and VALUE (USD) figures above them.
        if (data.column.index >= 5) {
          data.cell.styles.halign = 'right';
        }
        // "Total received" is pushed to the right edge of the TX HASH column so
        // it reads as a label for the two figures beside it.
        if (data.section === 'foot' && data.column.index === 4) {
          data.cell.styles.halign = 'right';
          data.cell.styles.textColor = MUTED;
        }
      },
      // The asset and network logos are drawn over their cells — autoTable has
      // no image cell type, so the text is padded left and the bitmap placed in
      // the gap.
      didDrawCell: (data) => {
        if (data.section !== 'body') return;
        const p = payments[data.row.index];
        if (!p) return;
        const y = data.cell.y + (data.cell.height - LOGO) / 2;
        const x = data.cell.x + 3;
        if (data.column.index === 2) {
          drawCellLogo(doc, logos.tokens[p.symbol], p.symbol, x, y, LOGO);
        } else if (data.column.index === 3) {
          drawCellLogo(
            doc,
            logos.chains[p.chain],
            chainNativeSymbol(p.chain) || chainDisplayLabel(p.chain),
            x,
            y,
            LOGO,
          );
        }
      },
    });

    ty = (doc.lastAutoTable?.finalY || ty) + 10;
  }

  // === Notes & (merchant's own) Terms ======================================
  // The client note and terms captured at invoice creation print after the
  // payment breakdown.
  //
  // These used to be pinned to the bottom of the page, dropped to sit just
  // above the footer. On a short invoice that left a page-high band of white
  // between the payment table and the note, which read as a rendering fault
  // rather than as layout. They now follow the content that precedes them.
  const LINE_H = 4.5;

  const renderNote = (heading, lines) => {
    // Keep the heading with at least its first line, never orphaned above a
    // page break.
    if (ty + 5.5 + LINE_H > BODY_BOTTOM) newPage();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(heading.toUpperCase(), margin, ty, { charSpace: 0.3 });
    ty += 5.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...BODY);

    // Line by line, so a note longer than a page continues onto the next one
    // instead of being drawn over the footer and off the sheet.
    for (const line of lines) {
      if (ty > BODY_BOTTOM) newPage();
      doc.text(line, margin, ty);
      ty += LINE_H;
    }
    ty += 8;
  };

  const notes = [
    ['Note', inv.metadata?.clientNote],
    ['Terms & Conditions', inv.metadata?.termsConditions],
  ]
    .filter(([, text]) => text)
    .map(([heading, text]) => {
      // Wrapped at the body font so the line count matches the render.
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      const lines = doc.splitTextToSize(pdfText(text), contentR - margin);
      return { heading, lines };
    });

  if (notes.length) {
    // Render from wherever the preceding section left the cursor. renderNote
    // still breaks pages as it goes, so a note longer than the remaining space
    // continues overleaf rather than running into the footer.
    notes.forEach((n) => renderNote(n.heading, n.lines));
  }

  // === Footer + fixed liability disclaimer (every page) ====================
  const footerRight = [businessName, pdfText(mer.email)].filter(Boolean).join('  \u00b7  ');
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    const fY = FOOTER_BASE;

    // Fixed disclaimer as fine print above the footer rule. Laid out upward
    // from the rule rather than downward from a fixed y, so if the text ever
    // wraps to more lines it grows away from the rule instead of through it.
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...FAINT);
    const disc = doc.splitTextToSize(LIABILITY_DISCLAIMER, contentR - margin);
    const DISC_LINE_H = 3;
    const discTop = fY + 2 - 3 - (disc.length - 1) * DISC_LINE_H;
    doc.text(disc, margin, discTop);

    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.3);
    doc.line(margin, fY + 2, contentR, fY + 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...FAINT);
    doc.text('Thank you for your business', margin, fY + 7);
    if (footerRight) doc.text(footerRight, contentR, fY + 7, { align: 'right' });
  }

  return doc;
}

// Browser entry point: build the document and trigger a download.
export async function generateInvoicePdf(invoice, merchant = {}, transactions = []) {
  const doc = await buildInvoiceDoc(invoice, merchant, transactions);
  doc.save(`${invoice?.id || 'invoice'}.pdf`);
}

export default generateInvoicePdf;
